import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { BiX } from 'react-icons/bi';
import { Bubble, Conversations, Sender } from '@ant-design/x';
import { Tooltip } from 'antd';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button, Text } from '@ydistri/ds';
import { addToast } from '@ydistri/utils';
import { formatDateTime, formatRelativeShort } from '@ydistri/generic-utils';
import { ConversationMessageRole, ConversationStatus, ScreenContext } from '@ydistri/api-sdk';
import { useSelector } from 'react-redux';
import { ReduxState, useAppDispatch } from '../../../store';
import { useSelectedProjectShortName } from '../../../hooks/useSelectedProjectShortName';
import {
  apiYbot,
  useCreateYbotConversationMutation,
  useCreateYbotMessageMutation,
  useGetYbotConversationsQuery,
  useGetYbotMessagesQuery,
  useResetYbotTurnMutation,
  ybotConversationTags,
} from './apiYbot';
import {
  confirmPendingConversation,
  rollbackPendingConversation,
  selectPendingConversation,
  setActiveConversationId,
} from './ybotSlice';
import YbotConversationMenu from './YbotConversationMenu';
import YbotMessageContext from './YbotMessageContext';
import AppMcpStatusIcon from './AppMcpStatusIcon';
import YdistriDevsMcpStatusIcon from './YdistriDevsMcpStatusIcon';
import { useYbotScreen } from './useYbotScreen';
import { parseWindowContext } from './ybotScreenLib';
import { clampYbotDockHeight, useYbotDock } from './useYbotDock';
import {
  directTools,
  emptyYbotStream,
  formatElapsed,
  isTurnOverdue,
  turnElapsedSeconds,
  YbotRunningTool,
  ybotStreamReducer,
} from './ybotStreamLib';
import {
  joinYbotConversation,
  leaveYbotConversation,
  onYbotReconnected,
  startYbotConnection,
  stopYbotConnection,
  ybotSignalrClient,
} from '../../../signalr/ybotClient';
import {
  DockBody,
  DockConversationLabel,
  DockConversationRow,
  DockConversations,
  DockConversationTitle,
  DockHeader,
  DockHeaderGroup,
  DockMain,
  DockMarkdown,
  DockPanel,
  DockPromptRow,
  DockResizeHandle,
  DockToolChip,
  DockToolChips,
  DockToolDot,
  DockTranscript,
} from './ybotDockStyledComponents';

// Carries its conversation: the queue outlives a switch by one render, and the drain must not
// send one conversation's question into another.
interface QueuedPrompt {
  conversationId: string;
  text: string;
  /**
   * The screen the question was written on, kept with it. A queued prompt is sent whenever the turn
   * before it ends, by which time the user may have navigated - and "which SKU is this?" is about
   * where they were when they asked, not where they ended up.
   */
  screen?: ScreenContext;
}

interface DockBubble {
  key: string;
  role: 'assistant' | 'user' | 'queued';
  content: string;
  loading?: boolean;
  /** The "sent from" marker, on user messages the server recorded a window context for. */
  footer?: React.ReactNode;
}

// `pre-wrap` keeps the line breaks a prompt was typed with; Markdown decides its own.
const plainContentStyle = { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' } as const;

// GFM because the agent writes tables, which plain CommonMark renders as a paragraph of pipes.
// Raw HTML stays unrendered (react-markdown's default) - the text comes from a model.
const markdownPlugins = [remarkGfm];

const renderMarkdown = (content: string): React.ReactNode => (
  <DockMarkdown>
    <Markdown remarkPlugins={markdownPlugins}>{content}</Markdown>
  </DockMarkdown>
);

const bubbleRoles = {
  user: { placement: 'end', variant: 'filled', styles: { content: plainContentStyle } },
  assistant: {
    placement: 'start',
    variant: 'outlined',
    messageRender: renderMarkdown,
    styles: { content: { overflowWrap: 'anywhere' } },
  },
  // Typed but not asked yet: same side as the user's own messages, visibly not sent.
  queued: {
    placement: 'end',
    variant: 'outlined',
    styles: { content: { ...plainContentStyle, opacity: 0.55 } },
  },
} as const;

const describeTool = (tool: YbotRunningTool): string => {
  const kind = tool.name === 'Agent' || tool.name === 'Task' ? 'subagent' : tool.name;
  return tool.label ? `${kind}: ${tool.label}` : kind;
};

// Renders nothing unless the server says ybot is available - a dock left open elsewhere must not
// reappear where the feature is off.
const YbotDock: React.FC = () => {
  const dispatch = useAppDispatch();
  const projectShortName = useSelectedProjectShortName();
  const screen = useYbotScreen();
  const { isOpen, height, turnTimeoutSeconds, close, setHeight } = useYbotDock();
  const pendingConversationId = useSelector(
    (state: ReduxState) => state.ybotReducer.pendingConversationId,
  );
  const activeConversationId = useSelector(
    (state: ReduxState) => state.ybotReducer.activeConversationId,
  );

  // Height mid-drag; localStorage is written once, on pointer release.
  const [draggedHeight, setDraggedHeight] = useState<number | undefined>(undefined);
  const dragRef = useRef<{ startY: number; startHeight: number } | undefined>(undefined);

  const [stream, dispatchStream] = useReducer(ybotStreamReducer, emptyYbotStream);
  const [prompt, setPrompt] = useState('');
  const [queuedPrompts, setQueuedPrompts] = useState<QueuedPrompt[]>([]);
  const [now, setNow] = useState(() => Date.now());

  // The hub handlers are registered once, so they read the current values through these refs.
  const activeConversationIdRef = useRef(activeConversationId);
  const streamRef = useRef(stream);

  // Freshness stamp: anything fetched before the dock opened was fetched with the hub stopped, so
  // a stale `Running` row must not be adopted - it would be an empty bubble over a finished answer.
  // The last turn each conversation was seen to end - only turns whose hub event actually arrived.
  const endedTurnsRef = useRef(new Map<string, string>());

  // When the conversation on screen last changed, so adoption can require data newer than that.
  const shownAtRef = useRef(0);

  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  useEffect(() => {
    streamRef.current = stream;
  }, [stream]);

  // currentData, not data, on both queries below: `data` sticks to the last successful result
  // across an arg change or a skip. For conversations that is not just a rendering blemish - the
  // auto-select effect further down reads `conversations` to pick "the newest conversation", and a
  // stale list from the project just left would make it select THAT project's conversation as if
  // it belonged to this one. For messages it would keep showing the previous conversation's
  // transcript once this one has none. The same bug `SkuSalePopover.tsx` guards against.
  const { currentData: conversations, fulfilledTimeStamp } = useGetYbotConversationsQuery(
    isOpen && projectShortName ? projectShortName : skipToken,
  );

  // The selection is optimistic while the create is in flight; everything that needs the row to
  // exist waits for it.
  const isPendingCreate =
    activeConversationId !== undefined && activeConversationId === pendingConversationId;
  const { currentData: messages, fulfilledTimeStamp: messagesFulfilledAt } =
    useGetYbotMessagesQuery(
      isOpen && activeConversationId && !isPendingCreate ? activeConversationId : skipToken,
    );

  const [createConversation, { isLoading: isCreating }] = useCreateYbotConversationMutation();
  const [createMessage] = useCreateYbotMessageMutation();
  const [resetTurn, { isLoading: isResetting }] = useResetYbotTurnMutation();

  const activeConversation = conversations?.find(
    conversation => conversation.id === activeConversationId,
  );
  const isRunning = activeConversation?.conversationStatusId === ConversationStatus.Running;

  // The server's status only turns `Running` after a refetch; the watched turn, set before anything
  // is sent, covers that window so a second prompt cannot start a competing turn.
  const isBusy =
    isRunning || (stream.turnId !== undefined && stream.conversationId === activeConversationId);

  const promptPlaceholder = ((): string => {
    if (!activeConversationId) {
      return 'Start a conversation to ask something';
    }

    return isBusy ? 'Ask the next question - it will be sent when this turn ends' : 'Ask ybot';
  })();

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      dragRef.current = { startY: event.clientY, startHeight: height };
      setDraggedHeight(height);
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [height],
  );

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) {
      return;
    }

    // The handle is on the top edge: dragging up makes the dock taller.
    setDraggedHeight(clampYbotDockHeight(drag.startHeight + (drag.startY - event.clientY)));
  }, []);

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      dragRef.current = undefined;
      event.currentTarget.releasePointerCapture(event.pointerId);

      if (drag && draggedHeight !== undefined) {
        setHeight(draggedHeight);
      }

      setDraggedHeight(undefined);
    },
    [draggedHeight, setHeight],
  );

  // A cancelled pointer must clear the drag, or plain hover over the handle keeps resizing.
  const handlePointerCancel = useCallback(() => {
    dragRef.current = undefined;
    setDraggedHeight(undefined);
  }, []);

  // Clearing a deleted selection lets the auto-select effect pick the newest remaining.
  const handleConversationDeleted = useCallback(
    (deletedId: string) => {
      if (deletedId === activeConversationIdRef.current) {
        dispatch(setActiveConversationId(undefined));
      }
    },
    [dispatch],
  );

  const handleNewConversation = useCallback(() => {
    if (!projectShortName) {
      return;
    }

    // The caller mints the id: the create answers 201 with no body, so there is nothing to read
    // back, and a retry of the same id is a clean 409 instead of a second conversation.
    const conversationId = crypto.randomUUID();
    dispatch(selectPendingConversation(conversationId));

    createConversation({ conversationId, projectShortName })
      .unwrap()
      .then(() => dispatch(confirmPendingConversation()))
      .catch(() => {
        // Nothing reads the mutation's error state, so the dock says it here. The optimistic
        // selection goes back; a new click mints a new id, so trying again is always right.
        dispatch(rollbackPendingConversation());
        dispatch(
          addToast({
            message: 'Ybot conversation could not be created',
            description: 'Please try again.',
            isError: true,
          }),
        );
      });
  }, [createConversation, dispatch, projectShortName]);

  const sendPrompt = useCallback(
    (text: string, sentFrom: ScreenContext | undefined = screen) => {
      if (!activeConversationId || !projectShortName || isPendingCreate) {
        return;
      }

      // Minted before the POST: a delta can reach the browser before the response does.
      const turnId = crypto.randomUUID();
      dispatchStream({
        type: 'watch',
        conversationId: activeConversationId,
        turnId,
        // Keeps the question on screen until the transcript refetch carries it.
        prompt: text,
        postedAt: Date.now(),
      });

      // Join before the post, or the first deltas go to a group this connection is not in yet.
      joinYbotConversation(activeConversationId)
        .then(() =>
          createMessage({
            conversationId: activeConversationId,
            projectShortName,
            prompt: text,
            turnId,
            // Where the question was written, which for anything but a queued prompt is also where
            // it is being sent from.
            screen: sentFrom,
          }).unwrap(),
        )
        .catch(() => {
          dispatchStream({ type: 'ended', conversationId: activeConversationId, turnId });
          dispatch(
            addToast({
              message: 'Ybot could not start the turn',
              description: 'The prompt was not accepted. Try again.',
              isError: true,
            }),
          );
        });
    },
    [activeConversationId, createMessage, dispatch, isPendingCreate, projectShortName, screen],
  );

  // A prompt typed while a turn runs is queued, not refused, and sent when the turn ends.
  const handleSubmit = useCallback(
    (submitted: string) => {
      const text = submitted.trim();
      if (!text || !activeConversationId || !projectShortName) {
        return;
      }

      setPrompt('');

      if (isBusy) {
        setQueuedPrompts(queue => [
          ...queue,
          { conversationId: activeConversationId, text, screen },
        ]);
        return;
      }

      sendPrompt(text);
    },
    [activeConversationId, isBusy, projectShortName, screen, sendPrompt],
  );

  const handleReset = useCallback(() => {
    if (!activeConversationId || !projectShortName) {
      return;
    }

    // A 409 (turn finished first) comes back as data, not an error; tags are invalidated either way.
    resetTurn({ conversationId: activeConversationId, projectShortName })
      .unwrap()
      .catch(() => {
        dispatch(
          addToast({
            message: 'Ybot could not reset the turn',
            isError: true,
          }),
        );
      });
  }, [activeConversationId, dispatch, projectShortName, resetTurn]);

  // On open or project switch the cache was filled while the hub was not listening: reset and
  // refetch, or a stale `Running` row becomes a bubble that never fills.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    dispatchStream({ type: 'reset' });
    setQueuedPrompts([]);
    endedTurnsRef.current.clear();
    dispatch(apiYbot.util.invalidateTags(ybotConversationTags(activeConversationIdRef.current)));
  }, [dispatch, isOpen, projectShortName]);

  // The connection lives exactly as long as the dock is open.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    startYbotConnection().catch(() => {
      dispatch(
        addToast({
          message: 'Ybot is not receiving live updates',
          description: 'The connection could not be opened. Replies will appear after a refresh.',
          isError: true,
        }),
      );
    });

    return () => {
      // Cleared on the way out, not on the next open: the drain reads the queue in the render it
      // runs in, so a clear scheduled by the opening effect lands one render too late to stop it.
      setQueuedPrompts([]);

      stopYbotConnection().catch(() => {
        // Stopping a connection that never opened is not worth reporting.
      });
    };
  }, [dispatch, isOpen]);

  // Registered once; the handlers filter by turn themselves.
  useEffect(() => {
    const onDelta = (conversationId: string, turnId: string, text: string) => {
      dispatchStream({ type: 'delta', conversationId, turnId, text });
    };

    const onCompleted = (conversationId: string, turnId: string) => {
      endedTurnsRef.current.set(conversationId, turnId);
      dispatchStream({ type: 'ended', conversationId, turnId });
      dispatch(apiYbot.util.invalidateTags(ybotConversationTags(conversationId)));
    };

    const onFailed = (conversationId: string, turnId: string, error: string) => {
      const watched = streamRef.current;
      endedTurnsRef.current.set(conversationId, turnId);
      dispatchStream({ type: 'ended', conversationId, turnId });
      dispatch(apiYbot.util.invalidateTags(ybotConversationTags(conversationId)));

      // Only the watched turn raises a toast: a superseded worker's late failure must not interrupt
      // the turn that replaced it, and it is persisted as a message anyway.
      if (watched.conversationId === conversationId && watched.turnId === turnId) {
        dispatch(addToast({ message: 'Ybot turn failed', description: error, isError: true }));
      }
    };

    const onToolStarted = (
      conversationId: string,
      turnId: string,
      toolId: string,
      name: string,
      label: string | null,
      parentToolId: string | null,
    ) => {
      dispatchStream({
        type: 'toolStarted',
        conversationId,
        turnId,
        toolId,
        name,
        label: label ?? undefined,
        parentId: parentToolId ?? undefined,
      });
    };

    const onToolEnded = (conversationId: string, turnId: string, toolId: string) => {
      dispatchStream({ type: 'toolEnded', conversationId, turnId, toolId });
    };

    // Distinct from onFailed: the owner's own reset must not read as an error.
    const onReset = (conversationId: string, turnId: string) => {
      endedTurnsRef.current.set(conversationId, turnId);
      dispatchStream({ type: 'ended', conversationId, turnId });
      dispatch(apiYbot.util.invalidateTags(ybotConversationTags(conversationId)));
    };

    // Carries nothing but "your list changed" - refetching is idempotent, so hearing this on top
    // of an acting client's own mutation invalidation is not doubled work.
    const onConversationsChanged = () => {
      dispatch(apiYbot.util.invalidateTags(ybotConversationTags(undefined)));
    };

    ybotSignalrClient.on('TurnDelta', onDelta);
    ybotSignalrClient.on('TurnCompleted', onCompleted);
    ybotSignalrClient.on('TurnFailed', onFailed);
    ybotSignalrClient.on('TurnToolStarted', onToolStarted);
    ybotSignalrClient.on('TurnToolEnded', onToolEnded);
    ybotSignalrClient.on('TurnReset', onReset);
    ybotSignalrClient.on('ConversationsChanged', onConversationsChanged);

    return () => {
      ybotSignalrClient.off('TurnDelta', onDelta);
      ybotSignalrClient.off('TurnCompleted', onCompleted);
      ybotSignalrClient.off('TurnFailed', onFailed);
      ybotSignalrClient.off('TurnToolStarted', onToolStarted);
      ybotSignalrClient.off('TurnToolEnded', onToolEnded);
      ybotSignalrClient.off('TurnReset', onReset);
      ybotSignalrClient.off('ConversationsChanged', onConversationsChanged);
    };
  }, [dispatch]);

  // A reconnect cannot replay missed deltas, so refetch and drop the buffer - pasting the two
  // halves together would render text the agent never wrote.
  useEffect(
    () =>
      onYbotReconnected(() => {
        dispatchStream({ type: 'reset' });
        dispatch(
          apiYbot.util.invalidateTags(ybotConversationTags(activeConversationIdRef.current)),
        );
      }),
    [dispatch],
  );

  useEffect(() => {
    if (!isOpen || !activeConversationId || isPendingCreate) {
      return;
    }

    joinYbotConversation(activeConversationId).catch(() => {
      // Already shows up as a 404 on the messages query.
    });

    return () => {
      leaveYbotConversation(activeConversationId).catch(() => {
        // The connection is going away anyway.
      });
    };
  }, [activeConversationId, isOpen, isPendingCreate]);

  // The in-flight bubble and the queue belong to the previous conversation now.
  useEffect(() => {
    dispatchStream({ type: 'reset' });
    setQueuedPrompts([]);
  }, [activeConversationId]);

  // Refetch as its conversation comes on screen - status as well as transcript. Switching away
  // leaves the conversation's hub group, so a turn that ended while it was off screen was announced
  // to nobody here and the cached row still reads Running.
  useEffect(() => {
    if (!isOpen || !activeConversationId) {
      return;
    }

    shownAtRef.current = Date.now();
    dispatch(apiYbot.util.invalidateTags(ybotConversationTags(activeConversationId)));
  }, [activeConversationId, dispatch, isOpen]);

  // Drains the queue when idle. Only prompts of the conversation on screen are sent - the queue can
  // still hold the previous conversation's for one render, until the clearing effect above runs.
  useEffect(() => {
    if (!isOpen || isBusy || !activeConversationId) {
      return;
    }

    const next = queuedPrompts.find(queued => queued.conversationId === activeConversationId);
    if (!next) {
      return;
    }

    setQueuedPrompts(queue => queue.filter(queued => queued !== next));
    sendPrompt(next.text, next.screen);
  }, [activeConversationId, isBusy, isOpen, queuedPrompts, sendPrompt]);

  // Adopts a turn already running (another tab, a reload).
  //
  // Fresh means fetched since this conversation came on screen, not merely since the dock opened:
  // switching away left its hub group, so a turn that ended meanwhile was announced to nobody here
  // and the cached row still reads Running. Waiting for the refetch that the effect above asks for
  // is the only thing that can tell the difference - the hub cannot, it never heard.
  useEffect(() => {
    if (!isOpen || activeConversation === undefined) {
      return;
    }

    const isRunning = activeConversation.conversationStatusId === ConversationStatus.Running;

    // Adoption can only ever add, so the server saying a turn is over has to be able to take it
    // away: a response fetched before the switch can settle after it, pass the freshness test and
    // adopt a turn that has already ended, and no hub event is left to clear it.
    const watched = streamRef.current;

    if (!isRunning && watched.conversationId === activeConversation.id && watched.turnId) {
      dispatchStream({
        type: 'ended',
        conversationId: activeConversation.id,
        turnId: watched.turnId,
      });
      return;
    }

    const isFresh = fulfilledTimeStamp !== undefined && fulfilledTimeStamp >= shownAtRef.current;

    if (
      isFresh &&
      isRunning &&
      activeConversation.turnId &&
      // A response already in flight when the hub said this turn ended must not re-watch it.
      endedTurnsRef.current.get(activeConversation.id) !== activeConversation.turnId
    ) {
      dispatchStream({
        type: 'watch',
        conversationId: activeConversation.id,
        turnId: activeConversation.turnId,
      });
    }
  }, [activeConversation, fulfilledTimeStamp, isOpen]);

  // Auto-select the newest conversation, but never fight a deliberate selection.
  useEffect(() => {
    if (isOpen && !activeConversationId && conversations && conversations.length > 0) {
      dispatch(setActiveConversationId(conversations[0].id));
    }
  }, [activeConversationId, conversations, dispatch, isOpen]);

  // Ticks only while a turn runs.
  useEffect(() => {
    if (!isOpen || !isRunning) {
      return;
    }

    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [isOpen, isRunning]);

  const conversationItems = useMemo(
    () =>
      (conversations ?? []).map(conversation => {
        // '' is how formatRelativeShort reports an unreadable date; it also guards formatDateTime
        // below, which throws on one.
        const when = formatRelativeShort(conversation.lastActivityAt);
        const title = conversation.title ?? 'New conversation';

        // One tooltip for the whole row: the full title plus the exact time.
        const hover = (
          <>
            <div>{title}</div>
            {when !== '' && <div>{formatDateTime(conversation.lastActivityAt)}</div>}
          </>
        );

        return {
          key: conversation.id,
          label: (
            <DockConversationRow>
              {/* Above the row: a tooltip to the right swallows clicks meant for the menu button. */}
              <Tooltip title={hover} placement="topLeft">
                <DockConversationLabel>
                  <DockConversationTitle>{title}</DockConversationTitle>
                  {when !== '' && (
                    <Text $type="light" $size="xSmall">
                      {when}
                    </Text>
                  )}
                </DockConversationLabel>
              </Tooltip>
              {projectShortName !== undefined && (
                <YbotConversationMenu
                  conversationId={conversation.id}
                  projectShortName={projectShortName}
                  title={title}
                  onDeleted={handleConversationDeleted}
                />
              )}
            </DockConversationRow>
          ),
        };
      }),
    [conversations, handleConversationDeleted, projectShortName],
  );

  const bubbles: DockBubble[] = useMemo(() => {
    const persisted: DockBubble[] = (messages ?? []).map(message => {
      const isUser = message.conversationMessageRoleId !== ConversationMessageRole.Assistant;

      // Only a question was asked from somewhere. An answer with a context stored on it would be a
      // server-side bug, and marking it "sent from" would be a lie either way.
      const context = isUser ? parseWindowContext(message.contextJson) : undefined;

      return {
        key: `message-${message.id}`,
        role: isUser ? 'user' : 'assistant',
        content: message.content,
        footer: context === undefined ? undefined : <YbotMessageContext context={context} />,
      };
    });

    const isShowingThisTurn =
      stream.turnId !== undefined && stream.conversationId === activeConversationId;

    // Our own question, until the transcript fetched after the post carries it. Retired by
    // timestamp, not by text - the same question can be asked twice.
    if (isShowingThisTurn && stream.prompt && (messagesFulfilledAt ?? 0) < stream.postedAt) {
      persisted.push({ key: `sent-${stream.turnId}`, role: 'user', content: stream.prompt });
    }

    // The in-flight reply; `loading` covers the gap before the first token.
    if (isShowingThisTurn) {
      persisted.push({
        key: `turn-${stream.turnId}`,
        role: 'assistant',
        content: stream.text,
        loading: stream.text === '',
      });
    }

    // Queued prompts, in the order they will be asked.
    queuedPrompts
      .filter(queued => queued.conversationId === activeConversationId)
      .forEach((queued, index) => {
        persisted.push({ key: `queued-${index}`, role: 'queued', content: queued.text });
      });

    return persisted;
  }, [
    activeConversationId,
    messages,
    messagesFulfilledAt,
    queuedPrompts,
    stream.conversationId,
    stream.postedAt,
    stream.prompt,
    stream.text,
    stream.turnId,
  ]);

  // Only the conversation on screen, and only the agent's own calls - see `directTools`.
  const runningTools = directTools(
    stream.conversationId === activeConversationId ? stream.tools : emptyYbotStream.tools,
  );

  const elapsed = isRunning ? turnElapsedSeconds(activeConversation.turnStartedAt, now) : undefined;
  const canReset =
    isRunning && isTurnOverdue(activeConversation.turnStartedAt, turnTimeoutSeconds, now);

  if (!isOpen) {
    return null;
  }

  return (
    <DockPanel
      // Pixels, not rem: the value comes from a pointer drag measured in CSS pixels.
      style={{ height: `${draggedHeight ?? height}px` }}
      data-type="YbotDock"
    >
      <DockResizeHandle
        role="separator"
        aria-label="Resize ybot"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
      />
      <DockHeader>
        <DockHeaderGroup>
          <Text $bold={true}>Ybot</Text>
          <Button
            $sizeVariant="xSmall"
            $colorVariant="ghostPrimary"
            onClick={handleNewConversation}
            disabled={isCreating || !projectShortName}
          >
            New conversation
          </Button>
          {elapsed !== undefined && <Text $type="light">Running {formatElapsed(elapsed)}</Text>}
          {runningTools.length > 0 && (
            <DockToolChips>
              {runningTools.map(tool => (
                <DockToolChip key={tool.id} title={describeTool(tool)}>
                  <DockToolDot />
                  {describeTool(tool)}
                </DockToolChip>
              ))}
            </DockToolChips>
          )}
          {canReset && (
            <Button
              $sizeVariant="xSmall"
              $colorVariant="ghostDanger"
              onClick={handleReset}
              disabled={isResetting}
            >
              Reset
            </Button>
          )}
        </DockHeaderGroup>
        <DockHeaderGroup>
          <AppMcpStatusIcon />
          <YdistriDevsMcpStatusIcon />
          <Button
            $sizeVariant="xSmall"
            $colorVariant="ghostPrimary"
            onClick={close}
            aria-label="Close ybot"
          >
            <BiX />
          </Button>
        </DockHeaderGroup>
      </DockHeader>
      <DockBody>
        <DockConversations>
          <Conversations
            items={conversationItems}
            activeKey={activeConversationId}
            onActiveChange={id => dispatch(setActiveConversationId(id))}
          />
        </DockConversations>
        <DockMain>
          <DockTranscript>
            <Bubble.List items={bubbles} roles={bubbleRoles} />
          </DockTranscript>
          <DockPromptRow>
            <Sender
              value={prompt}
              onChange={setPrompt}
              onSubmit={handleSubmit}
              // `loading` deliberately not passed: Sender would refuse to send, and a running turn
              // queues the next question rather than refusing it.
              // Disabled while the create is in flight too: handleSubmit clears the box before
              // sendPrompt can refuse, so an accepted-looking submit would eat the question.
              disabled={!activeConversationId || isPendingCreate}
              placeholder={promptPlaceholder}
            />
          </DockPromptRow>
        </DockMain>
      </DockBody>
    </DockPanel>
  );
};

export default YbotDock;
